import classNames from 'classnames';
import { Button } from '@praktio/common-ui';
import { debounce, difference } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import LoadDateTime from '../LoadDateTime/LoadDateTime';
import IconTooltip from '../../../ui/IconTooltip/IconTooltip';
import IconMaterial from '../../../ui/IconMaterial/IconMaterial';
import ActiveCourses from '../../../common/ActiveCourses/ActiveCourses';
import ActiveTeamsTree from '../../../common/ActiveTeamsTree/ActiveTeamsTree';

import { RootState } from '../../../../ducks/store';
import { useActiveTeams } from '../../../../hooks/useActiveTeams';
import {
  requestReportRun,
  selectHasLoadedReport,
  selectHasUnretryableFailure,
  selectIsFilterLocked,
  toggleReportSettingsPane,
} from '../../../../ducks/report';
import { useGetTeamsSlice } from '../../../../hooks/useGetTeamsSlice';
import { useGetCourseNames } from '../../../../hooks/useGetCourseNames';
import { useGetTeamCourses } from '../../../../hooks/useGetTeamCourses';
import { useActiveDeselectedCourses } from '../../../../hooks/useActiveDeselectedCourses';
import {
  toggleSelection,
  getCourseIdsFromCourseNames,
  getActiveTeams,
  getDisabledTeamIds,
} from '../../../../utils/reports';

import {
  buildTeamHierarchy,
  DEBOUNCE_DELAY,
  hasTeamManagerRole,
  LOCAL_STORAGE_KEYS,
  teamsOrDeselectedCoursesHaveChanged,
} from '../../../../utils';

import './ReportSettingsPane.scss';

interface Props {
  teams?: ITeam[];
}

const ReportSettingsPane = ({ teams = [] }: Props) => {
  // ===========================================================================
  // RTK Query
  // ===========================================================================

  const { fetchCourses, courses, error, isLoading, isFetching } = useGetCourseNames();

  // ===========================================================================
  // Redux
  // ===========================================================================

  const dispatch = useDispatch();

  const { activeTeams, reportSettingsPaneOpen } = useSelector((state: RootState) => {
    const user = state.auth.authenticatedUser;
    const isTeamManager = hasTeamManagerRole(user);
    const activeTeams = getActiveTeams(user, isTeamManager);

    return {
      activeTeams,
      reportSettingsPaneOpen: state.report.reportSettingsPaneOpen,
    };
  });

  // Locked while a report load is in flight or an auto-load is pending its batch.
  const isFilterLocked = useSelector(selectIsFilterLocked);

  // Whether results are actually on screen. Drives the one case where Run has nothing to do.
  const hasLoadedReport = useSelector(selectHasLoadedReport);

  // A lapsed session or lost access: running again returns the same failure, so Run is held back.
  const hasUnretryableFailure = useSelector(selectHasUnretryableFailure);

  // ===========================================================================
  // Hooks
  // ===========================================================================

  const { getTeamsCourseIds } = useGetTeamCourses();
  const { setActiveTeams, storeTeams } = useActiveTeams();

  const { currentTeamCourseIds, setCurrentTeamCourseIds, setActiveCourseNames } =
    useGetTeamsSlice();

  const { activeDeselectedCourses, setActiveDeselectedCourses, storeDeselectedCourses } =
    useActiveDeselectedCourses();

  // ===========================================================================
  // Derived values
  // ===========================================================================

  const treeData = buildTeamHierarchy({ teams, sorted: true }); // Format teams to display in ActiveTeamsTree
  const disabledIds = getDisabledTeamIds(treeData);
  const filteredInitialTeams = activeTeams.filter((id) => !disabledIds.has(id));

  // ===========================================================================
  // State
  // ===========================================================================

  const [teamSelectionLoading, setTeamSelectionLoading] = useState(false); // Used to show loading spinner when selecting teams
  const [selectedTeams, setSelectedTeams] = useState<string[]>(filteredInitialTeams);
  const [selectedTeamsPrev, setSelectedTeamsPrev] = useState<string[]>(filteredInitialTeams);
  const [deselectedCourses, setDeselectedCourses] = useState<string[]>(activeDeselectedCourses);
  const [savedTeams, setSavedTeams] = useState<string[]>(filteredInitialTeams);
  const [savedDeselectedCourses, setSavedDeselectedCourses] =
    useState<string[]>(activeDeselectedCourses);
  const [currentTeamCourseIdsLocal, setCurrentTeamCourseIdsLocal] =
    useState<string[]>(currentTeamCourseIds);

  // ===========================================================================
  // Other
  // ===========================================================================
  const isLoadingOrFetching = isLoading || isFetching;

  const noCoursesSelected = currentTeamCourseIdsLocal.every((courseId) =>
    deselectedCourses.includes(courseId),
  );

  // Nothing to run without a team and at least one of its courses, and not while the set of courses
  // for the current teams is still being resolved.
  const hasValidSelection = !!selectedTeams.length && !noCoursesSelected && !teamSelectionLoading;

  const hasTeamsOrCoursesChanged = teamsOrDeselectedCoursesHaveChanged(
    savedTeams,
    savedDeselectedCourses,
    selectedTeams,
    deselectedCourses,
  );

  // The one reason Run explains for being held back, or '' when it has none to give.
  //
  // Run stays live whenever there is no report on screen, even with nothing changed — a cancelled run
  // leaves the selections exactly as they were, and the canvas tells the user to press Run. Under the
  // old Save rule (changes required) that instruction pointed at a dead button.
  //
  // Stated once, with isRunDisabled derived from it, so the button's state and the tooltip explaining
  // it cannot drift apart. Expressed as two separate conditions they could: each branch is only
  // correct because the branches above it have already ruled out the other reasons, so reordering
  // them would silently blame the wrong one.
  //
  // The failure hold applies only while the selection is what failed. A changed selection may not
  // hit the same wall — a manager who lost access to one team can untick it and run the others — and
  // the flag behind the hold clears only when a run starts, so holding Run regardless would lock
  // that manager out on every visit, since the auto-load on arrival fails the same way each time.
  const getRunBlockedReason = (): string => {
    if (isFilterLocked) return 'Report is loading...';
    if (!hasValidSelection) return 'Please select at least one team and course.';
    if (hasUnretryableFailure && !hasTeamsOrCoursesChanged)
      return 'Sign in again to run this report';

    return '';
  };

  // The one hold with no tooltip: the report on screen already matches the panel, so there is
  // nothing to run. It used to say "Report is up to date", which reads as a claim about the data's
  // age when the results are only current as of the time shown above them, and it named no reason
  // the button was off. The state is visible on its own — the caption dates the results — so the
  // button is simply disabled.
  const isUpToDate = hasLoadedReport && !hasTeamsOrCoursesChanged;

  const tooltipMessage = getRunBlockedReason();
  const isRunDisabled = !!tooltipMessage || isUpToDate;

  // ===========================================================================
  // Debounced Functions
  // - We use these debounced functions to reduce the number of requests made
  //   to the server to get report data.
  // ===========================================================================

  const debouncedSetActiveTeams = useRef(
    debounce((activeTeams) => {
      storeTeams(activeTeams);
    }, DEBOUNCE_DELAY),
  ).current;

  const debouncedSetActiveDeselectedCourses = useRef(
    debounce(async (activeDeselectedCourses) => {
      await setActiveDeselectedCourses(activeDeselectedCourses);
      storeDeselectedCourses(activeDeselectedCourses);
    }, DEBOUNCE_DELAY),
  ).current;

  const fetchRequestIdRef = useRef(0);
  const inFlightFetchRef = useRef<ReturnType<typeof fetchCourses> | null>(null);

  const debouncedFetchCourses = useRef(
    debounce(async (teamIds) => {
      inFlightFetchRef.current?.abort();
      const requestId = ++fetchRequestIdRef.current;
      inFlightFetchRef.current = fetchCourses({ teamIds });

      const { data: courses } = await inFlightFetchRef.current;
      if (requestId !== fetchRequestIdRef.current) return;

      setCurrentTeamCourseIdsLocal(getCourseIdsFromCourseNames(courses));
      setTeamSelectionLoading(false);
    }, DEBOUNCE_DELAY),
  ).current;

  // ===========================================================================
  // Functions
  // ===========================================================================

  /**
   * Automatically select team courses when a team or teams are selected.
   * @param selectedTeams  - Array of selected team ids
   */
  const autoSelectTeamCourses = async (selectedTeams: string[]) => {
    setTeamSelectionLoading(true);

    // If no teams are selected, clear selected courses
    if (!selectedTeams.length) {
      setDeselectedCourses([]);
      return;
    }

    const addedTeams = difference(selectedTeams, selectedTeamsPrev); // Teams that were added
    const newTeamCoursesIds = getTeamsCourseIds(addedTeams); // Course IDs for newly selected teams only
    const newDeselectedCourses = difference(deselectedCourses, newTeamCoursesIds); // Remove IDs from deselected courses if they are in the new team courses

    setDeselectedCourses(newDeselectedCourses);
  };

  /**
   * Get courses for selected teams.
   */
  const getCourses = (selectedTeams: string[]) => {
    if (!selectedTeams.length) {
      debouncedFetchCourses([]); // Clear courses if no teams are selected
      return;
    }

    debouncedFetchCourses(selectedTeams);
  };

  const togglePane = () => {
    dispatch(toggleReportSettingsPane());
  };

  const updateActiveCourseNames = () => {
    const courseNames =
      courses
        ?.filter((course) => !deselectedCourses.includes(course._id))
        .map((course) => course.title) || [];

    setActiveCourseNames(courseNames);
  };

  // ===========================================================================
  // Handlers
  // ===========================================================================

  const handleSelectedTeamsChange = (selectedTeams: string[]) => {
    setSelectedTeams(selectedTeams);
    setSelectedTeamsPrev(selectedTeams);
    autoSelectTeamCourses(selectedTeams);
  };

  const handleCourseClick = (courseId: string) => {
    setDeselectedCourses((prev) => toggleSelection(prev, courseId));
  };

  const handleClearAllCourses = () => {
    const allCourseIds = getCourseIdsFromCourseNames(courses);
    setDeselectedCourses(allCourseIds);
  };

  const handleSelectAllCourses = () => setDeselectedCourses([]);

  const handleOnRun = () => {
    // First, before anything that could throw. Everything below is bookkeeping for the next render;
    // this is the part that actually loads the report, and it must not be reachable only by getting
    // through the rest of the handler intact.
    //
    // Said outright rather than left for the report pages to infer from the selection changing. Once
    // a run has already committed this selection, the dispatches below hand activeTeams and
    // activeDeselectedCourses the very same array objects they already hold; Immer treats writing an
    // identical reference as no write at all, so those two values — the ones the pages watch — keep
    // their references and no selection-watching effect fires. Run would do nothing. That is the
    // ordinary case here, since a cancelled run leaves the selection exactly as it was.
    //
    // Note this is narrower than "nothing in the store changes": updateActiveCourseNames below always
    // writes a freshly built array, so the slice itself does change on every press. Nothing that runs
    // the report watches it (the detail view reads it for its columns), which is why it does not
    // stand in for this dispatch.
    //
    // These dispatches batch, so a run that also changes the selection still reaches the pages as a
    // single update and runs once.
    dispatch(requestReportRun());

    setSavedTeams(selectedTeams);
    setSavedDeselectedCourses(deselectedCourses);

    setActiveTeams(selectedTeams);
    setActiveDeselectedCourses(deselectedCourses);
    setCurrentTeamCourseIds(currentTeamCourseIdsLocal);

    debouncedSetActiveTeams(selectedTeams);
    debouncedSetActiveDeselectedCourses(deselectedCourses);

    updateActiveCourseNames();
  };

  // ===========================================================================
  // Use Effects
  // ===========================================================================

  useEffect(() => {
    if (!teams.length) return;

    const filtered = selectedTeams.filter((id) => !disabledIds.has(id));

    if (filtered.length !== selectedTeams.length) {
      setSelectedTeams(filtered);
      setSelectedTeamsPrev(filtered);
      setSavedTeams(filtered);
    }

    setActiveTeams(filtered);
    setActiveDeselectedCourses(deselectedCourses);

    getCourses(filtered);

    // We don't want to run this effect when getCourses, selectedTeams or selectedCourses changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teams, dispatch]);

  useEffect(() => {
    getCourses(selectedTeams);

    // We don't want to run this effect when getCourses changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeams, dispatch]);

  // Remove active teams and deselected courses from local storage if they exist
  useEffect(() => {
    if (localStorage.getItem(LOCAL_STORAGE_KEYS.activeTeams)) {
      localStorage.removeItem(LOCAL_STORAGE_KEYS.activeTeams);
    }

    if (localStorage.getItem(LOCAL_STORAGE_KEYS.activeDeselectedCourses)) {
      localStorage.removeItem(LOCAL_STORAGE_KEYS.activeDeselectedCourses);
    }
  }, []);

  // ===========================================================================
  // Render
  // ===========================================================================

  const renderCourses = () => {
    const btnHandler = noCoursesSelected ? handleSelectAllCourses : handleClearAllCourses;

    return (
      <ActiveCourses
        noSubheader
        className="ReportSettingsPane_Courses"
        courses={courses}
        deselectedCourses={deselectedCourses}
        disabled={isFilterLocked}
        noCoursesSelected={noCoursesSelected}
        error={error as FetchBaseQueryError}
        isLoading={isLoadingOrFetching || teamSelectionLoading}
        onClearSelectClick={btnHandler}
        onCourseClick={handleCourseClick}
      />
    );
  };

  return (
    <>
      <button
        className={classNames('ReportSettingsPane_Btn', {
          'ReportSettingsPane_Btn--hide': !!reportSettingsPaneOpen,
        })}
        onClick={togglePane}
      >
        <IconMaterial name="tune" />
      </button>

      <div
        aria-busy={isFilterLocked}
        className={classNames('ReportSettingsPane_Pane', {
          'ReportSettingsPane_Pane--open': reportSettingsPaneOpen,
        })}
      >
        <header>
          <div>
            <h2>Report Settings</h2>

            <IconTooltip
              id="tooltip-courses"
              icon="help"
              placement="right"
              text="Select which teams and courses appear in your reports."
            />
          </div>

          <button onClick={togglePane}>
            <IconMaterial name="close" />
          </button>
        </header>

        {treeData ? (
          <ActiveTeamsTree
            noDefault
            showTooltip
            isSearchable
            isLocked={isFilterLocked}
            teams={teams}
            activeTeams={selectedTeams}
            subHeader="Teams"
            onSelectedChange={handleSelectedTeamsChange}
          />
        ) : (
          <p>No teams found.</p>
        )}

        <hr />

        {renderCourses()}

        <hr />

        <footer>
          <LoadDateTime />

          <Button
            aria-disabled={isRunDisabled}
            disabled={isRunDisabled}
            tooltip={tooltipMessage}
            tooltipPlacement="right"
            onClick={handleOnRun}
          >
            Run
          </Button>
        </footer>
      </div>
    </>
  );
};

export default ReportSettingsPane;
